/**
 * CINTURON DE SEGURIDAD DEL REVEAL.
 *
 * Un reveal que depende solo de `IntersectionObserver` deja el contenido
 * INVISIBLE en cualquier contexto donde nadie hace scroll: una captura
 * `fullPage` de Playwright (que pinta mas alla del viewport sin mover el
 * scroll, asi que el observador no dispara nunca), una impresion, un
 * rastreador. Eso convierte un adorno en "la pagina no carga".
 *
 * Incidente real (20260725, ORACLE + CHARLIE + ECHO): las capturas de pagina
 * completa salian con TODAS las secciones en blanco menos la visible, y el
 * barrido de desbordes daba verde en falso porque se saltaba lo invisible.
 *
 * REGLA: si pasan `BELT_MS` sin UNA SOLA senal de que hay un humano delante
 * (scroll, rueda, puntero, tecla, tacto), se revela todo. Si llega la senal,
 * el cinturon se cancela para siempre y manda el observador: el humano ve la
 * animacion intacta. El fallo, si lo hay, cae del lado de "se ve todo", nunca
 * del lado de "la pagina esta en blanco".
 *
 * ESTE MODULO CUBRE UN SOLO CASO: que no haya nadie. El caso contrario — hay
 * humano, y hace scroll tan rapido que el observador no llega a ver cruzar un
 * bloque — lo cubre `revealSweep`, y por eso cancelar el cinturon "para
 * siempre" ya no deja a nadie colgado. Los dos seguros son complementarios: no
 * los unifiques sin leer el incidente que documenta cada uno.
 */

/** Margen antes de dar por hecho que no hay nadie delante. */
const BELT_MS = 1200;

/** Cualquiera de estas senales prueba que hay un humano y cancela el cinturon. */
const HUMAN_SIGNALS = ['scroll', 'wheel', 'pointerdown', 'pointermove', 'keydown', 'touchstart'] as const;

type Listener = () => void;

let tripped = false;
let cancelled = false;
let started = false;
let timer: ReturnType<typeof setTimeout> | undefined;
const waiting = new Set<Listener>();

function teardown() {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  for (const signal of HUMAN_SIGNALS) {
    window.removeEventListener(signal, onHumanSignal);
  }
}

function onHumanSignal() {
  cancelled = true;
  waiting.clear();
  teardown();
}

function trip() {
  tripped = true;
  const listeners = [...waiting];
  waiting.clear();
  teardown();
  for (const listener of listeners) listener();
}

function start() {
  if (started || typeof window === 'undefined') return;
  started = true;
  for (const signal of HUMAN_SIGNALS) {
    window.addEventListener(signal, onHumanSignal, { passive: true, once: true });
  }
  timer = setTimeout(trip, BELT_MS);
}

/**
 * Avisa cuando salta el cinturon. Devuelve la baja.
 * Si ya salto, llama al momento; si ya se cancelo, no llama nunca.
 */
export function onRevealBelt(listener: Listener): () => void {
  if (cancelled) return () => {};
  if (tripped) {
    listener();
    return () => {};
  }
  start();
  waiting.add(listener);
  return () => waiting.delete(listener);
}

/** Solo para tests: devuelve el modulo a su estado de arranque. */
export function resetRevealBelt() {
  teardown();
  waiting.clear();
  tripped = false;
  cancelled = false;
  started = false;
}
